import React, { useState } from "react";
import {
  educationData,
  certificationsData,
  awardsData,
  profileData,
} from "../../data/mock";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Separator } from "../ui/separator";
import { toast } from "sonner";
import {
  GraduationCap,
  Award,
  Trophy,
  Mail,
  Phone,
  MapPin,
  Linkedin,
  Send,
  ArrowUpRight,
  Terminal,
  Snowflake,
  CircuitBoard,
  BookOpen,
  Zap,
  Database,
  FileDown,
  FileText,
  Download,
} from "lucide-react";

/* ===== EDUCATION ===== */
const EducationSection = () => (
  <section id="education" className="relative">
    <div className="hex-pattern absolute inset-0 pointer-events-none" />
    <div className="section-container relative z-10">
      <div className="text-center mb-14">
        <span className="section-label justify-center">Credentials</span>
        <h2 className="section-title">Academic Foundation</h2>
        <div className="gradient-line mx-auto" />
      </div>

      {/* Education Cards */}
      <div className="grid sm:grid-cols-2 gap-5 max-w-3xl mx-auto mb-16">
        {educationData.map((edu) => (
          <div
            key={edu.id}
            className="glass-card p-6 group hover:border-[#06B6D4]/20 transition-all duration-300"
          >
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-[#06B6D4]/8 border border-[#06B6D4]/10 flex items-center justify-center shrink-0 group-hover:bg-[#06B6D4]/12 transition-colors">
                <GraduationCap size={18} className="text-[#06B6D4]" />
              </div>
              <div>
                <h4 className="text-sm font-bold font-['Figtree'] text-[#F1F5F9] mb-1">
                  {edu.degree}
                </h4>
                <p className="text-xs text-[#94A3B8] mb-1.5">{edu.institution}</p>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-[#475569] font-['Fira_Code']">
                    {edu.period}
                  </span>
                  <Badge
                    variant="outline"
                    className={`text-[10px] font-['Fira_Code'] ${
                      edu.status === "In Progress"
                        ? "border-emerald-500/25 text-emerald-400"
                        : "border-[#1E293B] text-[#64748B]"
                    }`}
                  >
                    {edu.status}
                  </Badge>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Certifications & Awards */}
      <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
        {/* Certifications */}
        <div>
          <div className="flex items-center gap-2 mb-5">
            <Award size={16} className="text-[#06B6D4]" />
            <h3 className="text-base font-bold font-['Figtree'] text-[#F1F5F9]">
              Certifications
            </h3>
          </div>
          <div className="space-y-3">
            {certificationsData.map((cert) => {
              const iconMap = { Snowflake: Snowflake, CircuitBoard: CircuitBoard };
              const CertIcon = iconMap[cert.icon] || BookOpen;
              return (
                <div
                  key={cert.id}
                  className="flex items-center gap-3 p-4 rounded-xl bg-[#0A1628] border border-[#1E293B]/60 hover:border-[#06B6D4]/15 transition-all duration-200 group"
                >
                  <div className="w-8 h-8 rounded-lg bg-[#06B6D4]/8 flex items-center justify-center group-hover:bg-[#06B6D4]/12 transition-colors">
                    <CertIcon size={14} className="text-[#06B6D4]" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#F1F5F9]">{cert.name}</p>
                    <p className="text-[10px] text-[#475569] font-['Fira_Code']">
                      {cert.issuer}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Awards */}
        <div>
          <div className="flex items-center gap-2 mb-5">
            <Trophy size={16} className="text-[#F59E0B]" />
            <h3 className="text-base font-bold font-['Figtree'] text-[#F1F5F9]">
              Awards
            </h3>
          </div>
          <div className="space-y-3">
            {awardsData.map((award) => (
              <div
                key={award.id}
                className="flex items-center gap-3 p-4 rounded-xl bg-[#0A1628] border border-[#1E293B]/60 hover:border-[#F59E0B]/15 transition-all duration-200 group"
              >
                <div className="w-8 h-8 rounded-lg bg-[#F59E0B]/8 flex items-center justify-center group-hover:bg-[#F59E0B]/12 transition-colors">
                  <Trophy size={14} className="text-[#F59E0B]" />
                </div>
                <div>
                  <p className="text-sm font-medium text-[#F1F5F9]">{award.name}</p>
                  <p className="text-[10px] text-[#475569] font-['Fira_Code']">
                    {award.issuer}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ===== DOWNLOADS ===== */
const DownloadsSection = () => (
  <section className="relative">
    <div className="section-container py-16 md:py-20">
      <div className="terminal-card max-w-3xl mx-auto">
        <div className="terminal-header">
          <div className="terminal-dot bg-[#EF4444]" />
          <div className="terminal-dot bg-[#F59E0B]" />
          <div className="terminal-dot bg-[#22C55E]" />
          <span className="ml-3 text-[10px] text-[#475569] font-['Fira_Code']">
            downloads/documents.sh
          </span>
        </div>
        <div className="p-6 md:p-8">
          <div className="text-center mb-6">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Download size={18} className="text-[#06B6D4]" />
              <h3 className="text-lg font-bold font-['Figtree'] text-[#F1F5F9]">
                Download Documents
              </h3>
            </div>
            <p className="text-xs text-[#475569] font-['Fira_Code']">
              Get a copy of my professional documents
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {/* Resume */}
            <a
              href={profileData.resumeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 p-5 rounded-xl bg-[#060A14] border border-[#1E293B] hover:border-[#06B6D4]/25 transition-all duration-300 group"
            >
              <div className="w-12 h-12 rounded-xl bg-[#06B6D4]/8 border border-[#06B6D4]/15 flex items-center justify-center group-hover:bg-[#06B6D4]/15 transition-colors">
                <FileText size={20} className="text-[#06B6D4]" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-[#F1F5F9] font-['Figtree']">Resume</p>
                <p className="text-[10px] text-[#475569] font-['Fira_Code'] mt-0.5">
                  Professional summary — PDF
                </p>
              </div>
              <FileDown
                size={16}
                className="text-[#334155] group-hover:text-[#06B6D4] group-hover:animate-bounce transition-colors"
              />
            </a>

            {/* CV */}
            <a
              href={profileData.cvUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 p-5 rounded-xl bg-[#060A14] border border-[#1E293B] hover:border-[#F59E0B]/25 transition-all duration-300 group"
            >
              <div className="w-12 h-12 rounded-xl bg-[#F59E0B]/8 border border-[#F59E0B]/15 flex items-center justify-center group-hover:bg-[#F59E0B]/15 transition-colors">
                <FileText size={20} className="text-[#F59E0B]" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-[#F1F5F9] font-['Figtree']">Curriculum Vitae</p>
                <p className="text-[10px] text-[#475569] font-['Fira_Code'] mt-0.5">
                  Detailed background — PDF
                </p>
              </div>
              <FileDown
                size={16}
                className="text-[#334155] group-hover:text-[#F59E0B] group-hover:animate-bounce transition-colors"
              />
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ===== CONTACT ===== */
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [sending, setSending] = useState(false);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSending(true);
    setTimeout(() => {
      toast.success("Message transmitted successfully!");
      setFormData({ name: "", email: "", subject: "", message: "" });
      setSending(false);
    }, 1200);
  };

  return (
    <section id="contact" className="relative">
      <div className="section-container">
        <div className="text-center mb-14">
          <span className="section-label justify-center">Connect</span>
          <h2 className="section-title">Initialize Connection</h2>
          <p className="section-subtitle mx-auto">
            Ready to collaborate on your next data project? Send a transmission.
          </p>
          <div className="gradient-line mx-auto mt-4" />
        </div>

        <div className="grid md:grid-cols-[1fr_1.2fr] gap-10 max-w-4xl mx-auto">
          {/* Contact info */}
          <div className="space-y-5">
            <div className="glass-card p-6">
              <div className="flex items-center gap-2 mb-4">
                <Terminal size={14} className="text-[#06B6D4]" />
                <h4 className="text-sm font-bold font-['Figtree'] text-[#F1F5F9]">
                  Contact Endpoints
                </h4>
              </div>
              <div className="space-y-4">
                <a
                  href={`mailto:${profileData.email}`}
                  className="flex items-center gap-3 text-sm text-[#94A3B8] hover:text-[#06B6D4] transition-colors duration-200 group"
                >
                  <div className="w-8 h-8 rounded-lg bg-[#06B6D4]/8 flex items-center justify-center group-hover:bg-[#06B6D4]/12 transition-colors">
                    <Mail size={14} className="text-[#06B6D4]" />
                  </div>
                  <span className="text-xs font-['Fira_Code']">{profileData.email}</span>
                </a>
                <a
                  href={`tel:${profileData.phone}`}
                  className="flex items-center gap-3 text-sm text-[#94A3B8] hover:text-[#06B6D4] transition-colors duration-200 group"
                >
                  <div className="w-8 h-8 rounded-lg bg-[#06B6D4]/8 flex items-center justify-center group-hover:bg-[#06B6D4]/12 transition-colors">
                    <Phone size={14} className="text-[#06B6D4]" />
                  </div>
                  <span className="text-xs font-['Fira_Code']">{profileData.phone}</span>
                </a>
                <div className="flex items-center gap-3 text-sm text-[#94A3B8]">
                  <div className="w-8 h-8 rounded-lg bg-[#06B6D4]/8 flex items-center justify-center">
                    <MapPin size={14} className="text-[#06B6D4]" />
                  </div>
                  <span className="text-xs font-['Fira_Code']">{profileData.location}</span>
                </div>
              </div>
            </div>

            <a
              href={profileData.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="glass-card p-4 flex items-center gap-3 group hover:border-[#06B6D4]/20 transition-all duration-300"
            >
              <div className="w-8 h-8 rounded-lg bg-[#0A66C2]/10 flex items-center justify-center">
                <Linkedin size={14} className="text-[#0A66C2]" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-[#F1F5F9]">LinkedIn</p>
                <p className="text-[10px] text-[#475569] font-['Fira_Code']">
                  Connect with me
                </p>
              </div>
              <ArrowUpRight
                size={14}
                className="text-[#475569] group-hover:text-[#06B6D4] transition-colors"
              />
            </a>
          </div>

          {/* Contact Form - Terminal Style */}
          <div className="terminal-card">
            <div className="terminal-header">
              <div className="terminal-dot bg-[#EF4444]" />
              <div className="terminal-dot bg-[#F59E0B]" />
              <div className="terminal-dot bg-[#22C55E]" />
              <span className="ml-3 text-[10px] text-[#475569] font-['Fira_Code']">
                contact/new-message.sh
              </span>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] text-[#475569] mb-1.5 font-['Fira_Code']">
                    name *
                  </label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your name"
                    className="bg-[#060A14] border-[#1E293B] text-[#E2E8F0] placeholder:text-[#334155] focus:border-[#06B6D4]/40 focus:ring-[#06B6D4]/10 font-['IBM_Plex_Sans'] text-sm"
                  />
                </div>
                <div>
                  <label className="block text-[10px] text-[#475569] mb-1.5 font-['Fira_Code']">
                    email *
                  </label>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    className="bg-[#060A14] border-[#1E293B] text-[#E2E8F0] placeholder:text-[#334155] focus:border-[#06B6D4]/40 focus:ring-[#06B6D4]/10 font-['IBM_Plex_Sans'] text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[10px] text-[#475569] mb-1.5 font-['Fira_Code']">
                  subject
                </label>
                <Input
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="What's this about?"
                  className="bg-[#060A14] border-[#1E293B] text-[#E2E8F0] placeholder:text-[#334155] focus:border-[#06B6D4]/40 focus:ring-[#06B6D4]/10 font-['IBM_Plex_Sans'] text-sm"
                />
              </div>
              <div>
                <label className="block text-[10px] text-[#475569] mb-1.5 font-['Fira_Code']">
                  message *
                </label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell me about your project..."
                  rows={5}
                  className="bg-[#060A14] border-[#1E293B] text-[#E2E8F0] placeholder:text-[#334155] focus:border-[#06B6D4]/40 focus:ring-[#06B6D4]/10 resize-none font-['IBM_Plex_Sans'] text-sm"
                />
              </div>
              <Button
                type="submit"
                disabled={sending}
                className="w-full bg-gradient-to-r from-[#06B6D4] to-[#0891B2] text-[#F1F5F9] hover:from-[#0891B2] hover:to-[#0E7490] font-semibold h-11 font-['IBM_Plex_Sans']"
              >
                {sending ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Transmitting...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Send size={15} />
                    Transmit Message
                  </span>
                )}
              </Button>
              <p className="text-[9px] text-[#1E293B] text-center font-['Fira_Code']">
                * Contact form currently uses MOCK data
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ===== FOOTER ===== */
const Footer = () => (
  <footer className="border-t border-[#0F1629]">
    <div className="max-w-[1200px] mx-auto px-6 py-8">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded bg-[#06B6D4]/15 border border-[#06B6D4]/20 flex items-center justify-center">
            <Database size={11} className="text-[#06B6D4]" />
          </div>
          <span className="text-xs text-[#475569] font-['Fira_Code']">
            {profileData.name}
          </span>
        </div>
        <div className="flex items-center gap-1.5 text-[10px] text-[#1E293B] font-['Fira_Code']">
          <span>Engineered with</span>
          <Zap size={10} className="text-[#06B6D4]" />
          <span>precision</span>
        </div>
        <div className="flex items-center gap-4">
          <a
            href={`mailto:${profileData.email}`}
            className="text-[#334155] hover:text-[#06B6D4] transition-colors"
          >
            <Mail size={15} />
          </a>
          <a
            href={profileData.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#334155] hover:text-[#06B6D4] transition-colors"
          >
            <Linkedin size={15} />
          </a>
          <a
            href={`tel:${profileData.phone}`}
            className="text-[#334155] hover:text-[#06B6D4] transition-colors"
          >
            <Phone size={15} />
          </a>
        </div>
      </div>
    </div>
  </footer>
);

/* ===== COMBINED EXPORT ===== */
const BottomSections = () => (
  <>
    <EducationSection />
    <DownloadsSection />
    <Separator className="max-w-[1200px] mx-auto bg-[#0F1629]" />
    <ContactSection />
    <Footer />
  </>
);

export default BottomSections;
